import { CarrierAgreementPanel } from '@/components/carrier-onboarding/CarrierAgreementPanel'
import { CarrierOnboardingWizard } from '@/components/carrier-onboarding/CarrierOnboardingWizard'
import { CarrierQuoteRespondPanel } from '@/components/carrier-onboarding/CarrierQuoteRespondPanel'
import {
  type CarrierApplication,
  type CarrierDocumentSlot,
  parseCarrierDocumentSlots,
  translateCarrierError,
} from '@/components/carrier-onboarding/shared'
import { CarrierApplicationSummary } from '@/components/carrier-onboarding/CarrierApplicationSummary'
import { LoadFailed } from '@/components/LoadFailed'
import { ApplicationRejectedScreen } from '@/components/onboarding/ApplicationRejectedScreen'
import { OnboardingLayout } from '@/components/OnboardingLayout'
import { TokenError } from '@/components/TokenError'
import { type TokenErrorReason } from '@/lib/tokenErrors'
import { useCarrierDocuments } from '@/hooks/queries/carrier-onboarding/useCarrierDocuments'
import { useCarrierInsuranceQuote } from '@/hooks/queries/carrier-onboarding/useCarrierInsuranceQuote'
import { useCarrierTokenValidation } from '@/hooks/queries/carrier-onboarding/useCarrierTokenValidation'
import { CARRIER_WIZARD_STEPS, type CarrierWizardStep } from '@repo/constants'
import { CenteredLoading } from '@repo/ui/components'
import { createFileRoute } from '@tanstack/react-router'
import { CheckCircleIcon } from 'lucide-react'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

// The step the applicant is LOOKING at, which is not the same thing as the
// resume point the server keeps (`ONB-16`): that one only ever advances, so a
// back-jump had nowhere to live and a refresh threw the applicant forward to
// the furthest step they had ever reached (PET-3866). Parking it in the URL
// makes a refresh a no-op and costs no schema change. `.catch(undefined)`
// because a hand-edited step is a reason to fall back to the resume point, not
// to fail the route.
const searchSchema = z.object({
  step: z.enum(CARRIER_WIZARD_STEPS).optional().catch(undefined),
})

export const Route = createFileRoute('/carrier-onboard/$token')({
  validateSearch: searchSchema,
  component: CarrierOnboardPage,
})

function CarrierOnboardPage() {
  const { token } = Route.useParams()
  const { step: viewedStep } = Route.useSearch()
  const navigate = Route.useNavigate()
  const { t } = useTranslation('ui')

  // `replace` so stepping through the wizard doesn't bury the page the
  // applicant arrived from under one history entry per step — Back keeps
  // meaning "leave the wizard", exactly as it did before the param existed.
  const setViewedStep = (step: CarrierWizardStep) => {
    void navigate({ search: { step }, replace: true })
  }

  const tokenQuery = useCarrierTokenValidation(token)
  const valid = !!(tokenQuery.data && 'valid' in tokenQuery.data && tokenQuery.data.valid)
  const documentsQuery = useCarrierDocuments(token, valid)

  useEffect(() => {
    if (documentsQuery.isError) {
      toast.error(
        translateCarrierError(documentsQuery.error, t('carrierOnboarding.documents.loadFailed'), t),
      )
    }
  }, [documentsQuery.isError, documentsQuery.error, t])

  if (tokenQuery.isLoading) {
    return (
      <OnboardingLayout titleKey="carrierOnboarding.title">
        <CenteredLoading />
      </OnboardingLayout>
    )
  }

  // A REQUEST that failed, not a verdict on the link. `/validate/:token` answers
  // an invalid token with a 200 carrying `valid: false`, so anything thrown here
  // is transport: a 429 off the shared 60-per-minute budget, a 500, a dropped
  // connection. Collapsing those into `reason: 'not_found'` is why an applicant
  // who had been clicking around the wizard was told, on the next refresh, that
  // their invitation link was invalid — a dead end, for a link that was fine.
  if (tokenQuery.isError) {
    return (
      <LoadFailed
        titleKey="carrierOnboarding.title"
        reason={translateCarrierError(tokenQuery.error, t('onboarding.error.loadFailed'), t)}
        onRetry={() => void tokenQuery.refetch()}
        isRetrying={tokenQuery.isFetching}
      />
    )
  }

  const data = tokenQuery.data
  if (!data || !('valid' in data) || !data.valid) {
    // `flow_unknown` has no applicant-facing meaning and still collapses to
    // 'not_found'; `revoked` is carried through, because "the office took this
    // link back" and "this link never existed" are different things to be told
    // (PET-3831).
    const raw = data && 'reason' in data ? data.reason : 'not_found'
    const reason: TokenErrorReason =
      raw === 'expired' || raw === 'used' || raw === 'revoked' || raw === 'tenant_inactive'
        ? raw
        : 'not_found'
    return <TokenError reason={reason} titleKey="carrierOnboarding.title" />
  }

  const application = data.application as CarrierApplication | null
  if (!application) {
    return <TokenError reason="not_found" titleKey="carrierOnboarding.title" />
  }

  if (application.status === 'approved') {
    return (
      <OnboardingLayout titleKey="carrierOnboarding.title">
        <div className="py-12 text-center">
          <CheckCircleIcon className="mx-auto size-12 text-green-600" />
          <p className="mt-4 text-sm text-muted-foreground">
            {t('carrierOnboarding.alreadyApproved')}
          </p>
        </div>
      </OnboardingLayout>
    )
  }
  if (application.status === 'rejected') {
    return (
      <OnboardingLayout titleKey="carrierOnboarding.title">
        <ApplicationRejectedScreen reason={application.rejectionReason} />
      </OnboardingLayout>
    )
  }
  if (application.status === 'submitted' || application.status === 'under_review') {
    // Even after submit the applicant still needs a way to accept/decline an
    // insurance quote the office pushed to the agent. When one is waiting
    // for the applicant's response we skip the generic "submitted" screen
    // and lead with the quote panel — otherwise the confirmation blocks the
    // actionable UI.
    return (
      <PostSubmitScreen
        token={token}
        application={application}
        slots={parseCarrierDocumentSlots(documentsQuery.data?.slots)}
        alreadySubmittedText={t('carrierOnboarding.alreadySubmitted')}
      />
    )
  }

  // Every effective requirement for this application, already narrowed by the
  // carrier's insurance path server-side. This used to be filtered down to a
  // hardcoded four-slug list, which hid the Insurance COI and the workers' comp
  // options and made the wizard contradict the document matrix.
  const slots = parseCarrierDocumentSlots(documentsQuery.data?.slots)

  return (
    <OnboardingLayout titleKey="carrierOnboarding.title">
      <div className="space-y-6">
        <header>
          <h1 className="text-2xl font-bold">{t('carrierOnboarding.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t('carrierOnboarding.subtitle', { tenantName: data.invitingTenant.name })}
          </p>
        </header>

        {data.priorRejectionReason && (
          <div className="rounded-md border border-amber-300 bg-amber-50 p-4">
            <p className="text-sm font-medium text-amber-900">
              {t('carrierOnboarding.resubmit.title')}
            </p>
            <p className="mt-1 text-sm text-amber-800">{t('carrierOnboarding.resubmit.body')}</p>
            <blockquote className="mt-2 border-l-4 border-amber-400 pl-3 text-sm text-amber-900 italic">
              {data.priorRejectionReason}
            </blockquote>
          </div>
        )}

        <CarrierOnboardingWizard
          token={token}
          application={application}
          slots={slots}
          viewedStep={viewedStep}
          onViewedStepChange={setViewedStep}
          onSubmitted={() => {
            void tokenQuery.refetch()
          }}
        />
      </div>
    </OnboardingLayout>
  )
}

function PostSubmitScreen({
  token,
  application,
  slots,
  alreadySubmittedText,
}: {
  token: string
  application: CarrierApplication
  slots: CarrierDocumentSlot[]
  alreadySubmittedText: string
}) {
  const quoteQuery = useCarrierInsuranceQuote(token, true)
  const submission = quoteQuery.data?.submission ?? null
  // When a live quote is sitting there waiting for a decision, that's the
  // primary CTA on this screen — the "we received your application" note
  // would just push the accept/decline UI below the fold on small viewports.
  const hideSubmittedNotice = submission?.status === 'quote_sent'

  return (
    <OnboardingLayout titleKey="carrierOnboarding.title">
      <div className="mx-auto max-w-xl space-y-6 py-12">
        {!hideSubmittedNotice && (
          <div className="text-center">
            <CheckCircleIcon className="mx-auto size-12 text-blue-600" />
            <p className="mt-4 text-sm text-muted-foreground">{alreadySubmittedText}</p>
          </div>
        )}
        <CarrierQuoteRespondPanel token={token} />
        {/* The agreement is the last thing owed before approval (PET-3819), and
            it lands here rather than in the wizard because it is only reachable
            after the office has reviewed everything — the same reason the quote
            round-trip lives on this screen (`CARR-25`). */}
        <CarrierAgreementPanel token={token} />
        {/* A recap of what they sent, not the wizard's last step again. The
            document sections inside it stay actionable: submitting is not the
            end of the road for uploads (PET-3194), so an office rejection is
            still answerable and a document that arrives late still lands. */}
        <div className="border-t pt-6">
          <CarrierApplicationSummary token={token} application={application} slots={slots} />
        </div>
      </div>
    </OnboardingLayout>
  )
}
